import { BlogGrid, type BlogGridItem } from '@/legacy/components/blog/BlogGrid'
import { PageSeo } from '@/components/seo/PageSeo'
import { sitePages } from '@/config/pages'
import { ListingSection } from '@/legacy/components/sections/ListingSection'
import { PartnerProofSection } from '@/legacy/components/sections/PartnerProofSection'
import type { CaseStudyCardItem } from '@/legacy/components/sections/CaseStudyCard'
import { getAllArticles } from '@/data/articles'
import type { Article, ArticleFact } from '@/domain/article'
import { articlePath } from '@/lib/url'

const caseStudyNames: Record<string, string> = {
  'ridge-paragon-case-study-preview': 'Douglas, Ridge Paragon',
}

const caseStudyOrder = ['ridge-paragon-case-study-preview']

const getFacts = (article: Article) => article.body.find((block) => block.type === 'facts')?.facts ?? []

const factValue = (facts: ArticleFact[], label: string) =>
  facts.find((fact) => fact.label.toLowerCase() === label)?.value

const compactMetricValue = (value: string) => value.replace('Tax and bookkeeping', 'Tax & bookkeeping')

const toArticleItem = (article: Article): BlogGridItem => ({
  title: article.title,
  excerpt: article.excerpt,
  to: articlePath(article.slug),
  tags: article.tags,
  image: article.coverImage,
})

const toCaseStudyItem = (article: Article): CaseStudyCardItem => {
  const facts = getFacts(article)

  return {
    ...toArticleItem(article),
    title: caseStudyNames[article.slug] ?? article.title,
    metrics: [
      { label: 'Service line', value: compactMetricValue(factValue(facts, 'service line') ?? 'Accounting') },
      { label: 'Clients', value: factValue(facts, 'clients') ?? '120' },
    ],
  }
}

const allArticles = getAllArticles()
const caseStudyItems = caseStudyOrder
  .map((slug) => allArticles.find((article) => article.slug === slug && article.format === 'case-study'))
  .filter((article): article is Article => Boolean(article))
  .map(toCaseStudyItem)
const articleItems = allArticles.filter((article) => article.format !== 'case-study').map(toArticleItem)

export const BlogPage = () => (
  <>
    <PageSeo page={sitePages.blog} />

    <div className="blog-page">
      <div className="blog-page__inner">
        <section className="blog-hero" aria-labelledby="blog-title">
          <div>
            <h1 id="blog-title">Blog</h1>
            <p>The latest news, stories, and insights on our mission to help accountants gain a foothold in the AI era.</p>
          </div>
        </section>

        <PartnerProofSection
          title="Meet our partners"
          titleId="blog-case-studies-title"
          items={caseStudyItems}
          variant="blog"
        />

        <ListingSection title="Articles" titleId="blog-articles-title">
          <BlogGrid items={articleItems} />
        </ListingSection>
      </div>
    </div>
  </>
)
